import React, { useState, useCallback, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import LanguageStringResolver from '../../fields/language/LanguageStringResolver';
import UploadPanel from '../../upload/UploadPanel';
import ConfirmationModal from '../../modals/ConfirmationModal';
import Logger, { LOG_LEVELS } from '../../../utils/logger';
import { parseECListingExcel } from '../../../utils/ecListingExcelParser';
import { MAX_UPLOAD_FILE_SIZE_MB, ALLOWED_FILE_EXTENSIONS } from '../../../utils/ecListingImportConfig';
import ReadOnlyWrapper from '../../authorization/ReadOnlyWrapper';

const log = new Logger('VAT_IMPORT_SECTION', LOG_LEVELS.DEBUG);

/**
 * VATImportSection - Component for importing VAT Number sales transactions
 * via Excel template upload in EC Listing Open Returns Details page.
 */
const VATImportSection = ({ onDataImported }) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * Handles file upload request - shows confirmation modal
   */
  const handleUploadRequest = useCallback((filesToBeSent) => {
    log.debug('handleUploadRequest: Files received', filesToBeSent);
    setUploadError(null);

    if (!filesToBeSent || filesToBeSent.length === 0) {
      return;
    }

    setPendingFile(filesToBeSent[0]);
    setShowConfirmModal(true);
  }, []);

  /**
   * Processes the file after user confirms
   */
  const processFile = useCallback(async () => {
    if (!pendingFile || !isMountedRef.current) return;

    setIsProcessing(true);
    setUploadError(null);

    try {
      log.debug('processFile: Processing file', pendingFile.name);
      const result = await parseECListingExcel(pendingFile);

      if (!isMountedRef.current) return;

      if (!result.success) {
        setUploadError(result.errors.join('\n'));
        log.error('processFile: Parsing errors', result.errors);
        return;
      }

      log.debug('processFile: Parsed lines', result.lines.length);
      onDataImported(result.lines);
    } catch (error) {
      log.error('processFile: Error', error);
      if (isMountedRef.current) {
        setUploadError(`Failed to process file: ${error.message}`);
      }
    } finally {
      if (isMountedRef.current) {
        setIsProcessing(false);
        setPendingFile(null);
      }
    }
  }, [pendingFile, onDataImported]);

  const handleConfirmYes = useCallback(() => {
    setShowConfirmModal(false);
    processFile();
  }, [processFile]);

  const handleConfirmNo = useCallback(() => {
    setShowConfirmModal(false);
    setPendingFile(null);
  }, []);

  const handleClearFiles = useCallback(() => {
    setPendingFile(null);
    setUploadError(null);
  }, []);

  return (
    <div className="vat-import-section mt30">
      <h3 className="mb15">
        <LanguageStringResolver string="ecListing_ImportVATSection_Title" />
      </h3>

      {/* Information Messages */}
      <div className="vat-import-messages mb20">
        <s-box>
          <ul className="list-disc pl20">
            <li className="mb10">
              <LanguageStringResolver string="ecListing_ImportVATSection_Message1" />
            </li>
            <li>
              <LanguageStringResolver string="ecListing_ImportVATSection_Message2" />
            </li>
            <li className="mb10">
              <LanguageStringResolver string="ecListing_ImportVATSection_Message3" />
            </li>
          </ul>
        </s-box>
      </div>

      <s-row>
        {/* Download Template Section */}
        <s-col span="6">
          <s-box>
            <h4 className="mb10">
              <LanguageStringResolver string="ecListing_ImportVATSection_DownloadTemplateTitle" />
            </h4>
            <p className="mb15">
              <LanguageStringResolver string="ecListing_ImportVATSection_DownloadTemplateText" />
            </p>
            <a className="button secondary mb10" href="/ec-listing-import-template.xls">
              <LanguageStringResolver string="ecListing_ImportVATSection_DownloadTemplateBtn" />
            </a>
          </s-box>
        </s-col>

        {/* Upload Section */}
        <s-col span="6">
          <label className="mb10">
            <strong style={{ color: 'red' }}>* </strong>
            <LanguageStringResolver string="ecListing_ImportVATSection_UploadLabel" />
          </label>
          <div className="vat-upload-panel-wrapper">
            <ReadOnlyWrapper>
              <UploadPanel
                uploadLimit={1}
                acceptTypes={ALLOWED_FILE_EXTENSIONS}
                onUploadRequest={handleUploadRequest}
                maxSizeInKB={MAX_UPLOAD_FILE_SIZE_MB * 1000}
                onClearFiles={handleClearFiles}
                disabled={isProcessing}
              />
            </ReadOnlyWrapper>
          </div>

          {uploadError && (
            <div className="mt10" style={{ color: 'red' }}>
              {uploadError}
            </div>
          )}

          {isProcessing && (
            <div className="processing-indicator mt10">
              <LanguageStringResolver string="ecListing_ImportVATSection_Processing" />
            </div>
          )}
        </s-col>
      </s-row>

      <ConfirmationModal
        isModalPopupOpen={showConfirmModal}
        modelTitle="ecListing_ImportVATSection_ConfirmTitle"
        modelMessage="ecListing_ImportVATSection_ConfirmMessage"
        onYesClickHandler={handleConfirmYes}
        onCancelClickHandler={handleConfirmNo}
        dataTestId="ec-listing-import-confirm"
      />
    </div>
  );
};


const mapStateToProps = (state) => ({
  languages: state.languages || {}
});

export default connect(mapStateToProps)(VATImportSection);
